<script setup lang="ts">
import { computed, ref } from 'vue';

import type { FormSchema, VFormRef } from '@modules/forms/components/VForm/types';
import type {
  ISettingsClickDomainsItemBase,
} from '@modules/general-settings/submodules/click-domains/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS,
} from '@modules/general-settings/submodules/click-domains/constants';
import {
  useSettingsClickDomainsAddMutation,
} from '@modules/general-settings/submodules/click-domains/mutations';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();

const form = ref<VFormRef>();
const {
  mutateAsync: addDomain,
  errors,
  clearError,
  isLoading,
} = useSettingsClickDomainsAddMutation();

const formSchema = computed<FormSchema<ISettingsClickDomainsItemBase>>(() => ({
  domain: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'ADD_DOMAIN.FORM.DOMAIN').value,
    required: true,
  },
}));

async function handleSubmit(value: ISettingsClickDomainsItemBase): Promise<void> {
  await addDomain({
    click_domain: value,
  });
  form.value?.reset();
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsClickDomainsAddPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'ADD_DOMAIN.HEADER')"
  >
    <VForm
      ref="form"
      :schema="formSchema"
      :is-loading="isLoading"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
      :custom-errors="errors"
      @interacted="clearError"
      @submit="handleSubmit"
    >
      <template #secondaryBtn>
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'ADD_DOMAIN.BACK')"
          :to="{ name: ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_LIST_ROUTE_NAME }"
        />
      </template>
    </VForm>
  </VCard>
</template>
